import { Button, cn, Modal, ModalContent } from '@heroui/react'
import { useCreateCheckoutSession, usePlans } from '@qse/api'
import { useLogout } from '@qse/auth-ui'
import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { usePageFocusRefetch } from '@qse/utils'
import { ArrowLeftIcon } from 'lucide-react'
import { useState } from 'react'

import PricingCardsContainer from '../../components/PricingCardsContainer'

const Billing = () => {
  const { user } = useUserStore()
  const {
    data: plansData,
    isLoading: isPlansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = usePlans(undefined, {
    enabled: user?.userRole !== UserRoleEnum.TENANT_USER,
  })

  const [isOpen, setIsOpen] = useState(true)

  // Use the page focus refetch hook to automatically refetch plans data
  usePageFocusRefetch(() => {
    if (user?.userRole !== UserRoleEnum.TENANT_USER) {
      refetchPlans()
    }
  })

  const formatPrice = (amount: number): string => {
    const price = amount / 100 // Convert from cents

    return price.toFixed(0) // Remove decimals for clean display
  }

  const createCheckoutSession = useCreateCheckoutSession()
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)

  const handleSelectPlan = async (planId: string) => {
    try {
      setLoadingPlanId(planId)
      const response = await createCheckoutSession.mutateAsync({
        planId,
      })

      if (response?.url) {
        window.location.href = response.url
      }
    } catch (_error) {
      // Handle error silently or show user-friendly message
    } finally {
      setLoadingPlanId(null)
    }
  }

  const { handleLogout } = useLogout()

  return (
    <>
      <Modal
        hideCloseButton
        backdrop="blur"
        isOpen={isOpen}
        scrollBehavior="outside"
        classNames={{
          base: cn('max-w-full bg-transparent border-none outline-none !m-0 shadow-none'),
          header: '',
          body: '!p-0',
        }}
        onClose={() => setIsOpen(true)}
        onOpenChange={setIsOpen}
      >
        <ModalContent className="!p-0">
          <div className="fixed right-10 top-6 z-10">
            <Button
              className="bg-white text-black"
              endContent={<ArrowLeftIcon className="w-4 h-4 rotate-180" />}
              radius="sm"
              size="lg"
              onPress={() => handleLogout()}
            >
              Logout
            </Button>
          </div>
          <section className="px-4 py-8 min-h-screen flex flex-col items-center justify-center backdrop-blur-3xl">
            {plansData?.data && plansData.data.length > 0 && (
              <div className="text-center mb-8">
                <h1 className="text-5xl text-gray-900 font-light dark:text-white mb-4">
                  Welcome aboard, <span className="font-medium">{user?.firstName}!</span>
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                  Let's set up your billing plan to unlock your workspace.
                </p>
              </div>
            )}

            <PricingCardsContainer
              error={plansError}
              formatPrice={formatPrice}
              isLoading={isPlansLoading}
              loadingPlanId={loadingPlanId}
              plans={plansData?.data || []}
              onRetry={() => refetchPlans()}
              onSelectPlan={handleSelectPlan}
            />
          </section>
        </ModalContent>
      </Modal>
    </>
  )
}

export default Billing
